import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { useState } from "react"
import { z } from "zod"

import {
  loginWithPasswordAction,
  loginWithPasswordFormSchema,
  requestOtpAction,
  requestOtpFormSchema,
  requireGuest,
  verifyOtpAction,
  verifyOtpFormSchema,
} from "@/auth/rpc"
import config from "@/config"
import { useFormAction } from "@/form"
import { Button } from "@/ui/button"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

const loginSearchSchema = z.object({
  redirectTo: z.string().optional().catch("/"),
})

export const Route = createFileRoute("/login")({
  validateSearch: (search) => loginSearchSchema.parse(search),
  beforeLoad: async () => {
    await requireGuest()
  },
  loaderDeps: ({ search }) => ({ redirectTo: search.redirectTo ?? "/" }),
  loader: async ({ deps }) => ({ redirectTo: deps.redirectTo }),
  component: Login,
})

function Login() {
  const { redirectTo } = Route.useLoaderData()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [devCode, setDevCode] = useState<string | undefined>()
  const [showPassword, setShowPassword] = useState(false)

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold">{config.core.projectName}</h1>
          <p className="text-muted-foreground text-sm">
            {sentTo
              ? `We sent a 6-digit code to ${sentTo}`
              : showPassword
                ? "Sign in with your password"
                : "Sign in with your email"}
          </p>
        </div>

        {sentTo ? (
          <VerifyStep
            email={sentTo}
            redirectTo={redirectTo}
            devCode={devCode}
            onBack={() => {
              setSentTo(null)
              setDevCode(undefined)
            }}
          />
        ) : showPassword ? (
          <PasswordStep redirectTo={redirectTo} onBack={() => setShowPassword(false)} />
        ) : (
          <RequestStep
            redirectTo={redirectTo}
            onSent={(email, code) => {
              setSentTo(email)
              setDevCode(code)
            }}
            onPasswordClick={() => setShowPassword(true)}
          />
        )}
      </div>
    </main>
  )
}

function RequestStep({
  redirectTo,
  onSent,
  onPasswordClick,
}: {
  redirectTo: string
  onSent: (email: string, devCode?: string) => void
  onPasswordClick: () => void
}) {
  const { data, error, isPending, onSubmit } = useFormAction(
    requestOtpFormSchema,
    requestOtpAction,
    {
      onSuccess: (result) => {
        if (result && "sent" in result && result.sent) {
          onSent(result.email, result.devCode)
        }
      },
    },
  )

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email address</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          autoFocus
          required
          placeholder="you@agency.com"
          className="min-h-11 text-base"
        />
        {data && "error" in data && data.error && (
          <p className="text-destructive text-sm">{data.error}</p>
        )}
        {error && <p className="text-destructive text-sm">{error.message}</p>}
      </div>
      <Button type="submit" className="min-h-11 w-full" disabled={isPending}>
        {isPending ? "Sending…" : "Send code"}
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="border-muted w-full border-t" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-background text-muted-foreground px-2">or</span>
        </div>
      </div>

      <Button type="button" onClick={onPasswordClick} variant="outline" className="min-h-11 w-full">
        Sign in with password
      </Button>
    </form>
  )
}

function PasswordStep({ redirectTo, onBack }: { redirectTo: string; onBack: () => void }) {
  const { data, error, isPending, onSubmit } = useFormAction(
    loginWithPasswordFormSchema,
    loginWithPasswordAction,
    {
      onSuccess: () => {
        // Redirect happens via redirect() throw in the action
      },
    },
  )

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <input type="hidden" name="redirectTo" value={redirectTo} />

      <div className="space-y-2">
        <Label htmlFor="email-password">Email address</Label>
        <Input
          id="email-password"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          autoFocus
          required
          placeholder="you@agency.com"
          className="min-h-11 text-base"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          placeholder="••••••••"
          className="min-h-11 text-base"
        />
        {data && "error" in data && data.error && (
          <p className="text-destructive text-sm">{data.error}</p>
        )}
        {error && <p className="text-destructive text-sm">{error.message}</p>}
      </div>

      <Button type="submit" className="min-h-11 w-full" disabled={isPending}>
        {isPending ? "Signing in…" : "Sign in"}
      </Button>

      <div className="flex justify-center text-sm">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:underline">
          Use email code instead
        </button>
      </div>
    </form>
  )
}

function VerifyStep({
  email,
  redirectTo,
  devCode,
  onBack,
}: {
  email: string
  redirectTo: string
  devCode?: string
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const resend = useServerFn(requestOtpAction)
  const { data, error, isPending, onSubmit } = useFormAction(verifyOtpFormSchema, verifyOtpAction, {
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <input type="hidden" name="email" value={email} />
      <input type="hidden" name="redirectTo" value={redirectTo} />

      <div className="space-y-2">
        <Label htmlFor="code">6-digit code</Label>
        <Input
          id="code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          autoFocus
          required
          placeholder="000000"
          className="min-h-11 text-center text-lg tracking-[0.4em]"
        />
        {devCode && (
          <p className="text-muted-foreground text-xs">
            No mail provider configured — your code is <strong>{devCode}</strong>
          </p>
        )}
        {data && "error" in data && data.error && (
          <p className="text-destructive text-sm">{data.error}</p>
        )}
        {error && <p className="text-destructive text-sm">{error.message}</p>}
      </div>

      <Button type="submit" className="min-h-11 w-full" disabled={isPending}>
        {isPending ? "Verifying…" : "Sign in"}
      </Button>

      <div className="flex justify-between text-sm">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:underline">
          Use a different email
        </button>
        <button
          type="button"
          onClick={() => {
            const formData = new FormData()
            formData.set("email", email)
            void resend({ data: formData })
          }}
          className="text-muted-foreground hover:underline"
        >
          Resend code
        </button>
      </div>
    </form>
  )
}
